/*
 * Find My Blaster - quiz logic.
 *
 * One modal per page, rendered by snippets/gbu-find-my-blaster.liquid at the end of
 * layout/theme.liquid. Anything with data-fmb-open opens it, and so does a plain
 * document.dispatchEvent(new CustomEvent('open-quiz')) - that is the event name the old
 * header block already used, so its launcher keeps working without knowing about this file.
 *
 * Both listeners sit on document, never on the launcher element. The header re-renders
 * itself after load and again while the menu is in use, which would take any listener bound
 * to an element inside it with it.
 *
 * The catalogue is fetched once, on first open, from the quiz-data view. Every question is
 * then answered client side, which is what lets an option be counted - and hidden - before
 * it is offered. That is the dead end v1 walked people into: a beginner with $700 was sent
 * to an empty collection page, because nothing beginner-rated costs over $443.
 */
(function () {
  if (window.__gbuFmb) return;
  window.__gbuFmb = true;

  var KEYS = ['A', 'B', 'C', 'D', 'E'];

  // One mark per answer. Geometry only - stroked with currentColor so they take the row's
  // colour, including when the row is selected. A wall of text was the main reason this read
  // as a form rather than something to do with blasters.
  var P = {
    spark: '<path d="M12 3l2.1 5.9L20 11l-5.9 2.1L12 19l-2.1-5.9L4 11l5.9-2.1z"/>',
    bars: '<path d="M5 19v-7M12 19V6M19 19v-4"/>',
    target: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3.4"/>',
    tag: '<path d="M4 12.5l8-8.5h7v7l-8 8.5z"/><circle cx="15.3" cy="8.7" r="1.2"/>',
    wallet: '<rect x="3" y="6" width="18" height="12" rx="2.5"/><path d="M16 12h3"/>',
    gem: '<path d="M12 3l9 7-9 11-9-11z"/><path d="M3 10h18"/>',
    bolt: '<path d="M13 2L5 14h6l-1 8 8-12h-6z"/>',
    flame: '<path d="M12 22c3.9 0 6-2.7 6-6 0-5-6-8-6-14 0 0-6 4.2-6 10 0 3.3 2.1 6 6 6z"/>',
    crank: '<path d="M20 12a8 8 0 11-3-6.2"/><path d="M20 4v5h-5"/>',
    shield: '<path d="M12 3l8 3v6c0 5-3.5 8.2-8 9-4.5-.8-8-4-8-9V6z"/>',
    leaf: '<path d="M20 4C10 4 4 9 4 17v3h3c8 0 12-6 13-16z"/><path d="M8 16c3-3 6-5 10-7"/>',
    layers: '<path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/>',
    gaugeLow: '<path d="M4 17a8 8 0 1116 0"/><path d="M12 17L7.5 12.5"/>',
    gaugeMid: '<path d="M4 17a8 8 0 1116 0"/><path d="M12 17V10"/>',
    gaugeHigh: '<path d="M4 17a8 8 0 1116 0"/><path d="M12 17l4.5-4.5"/>'
  };
  var ICONS = {
    experience: [P.spark, P.bars, P.target],
    budget: [P.tag, P.wallet, P.gem],
    power: [P.bolt, P.flame, P.crank],
    material: [P.shield, P.leaf, P.layers],
    fps: [P.gaugeLow, P.gaugeMid, P.gaugeHigh]
  };

  function icon(key, i) {
    var d = (ICONS[key] || [])[i];
    if (!d) return '';
    return '<svg class="fmb-ico" width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + d + '</svg>';
  }

  var calmly = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Each answer carries the metafield values it accepts, plus the storefront filter it maps
  // to for the "browse all" link on the results screen. `rail` is the one-word name the left
  // rail uses, which has to stay short enough not to wrap.
  var QUESTIONS = [
    {
      key: 'experience',
      rail: 'Player',
      title: 'Who is this blaster for?',
      filter: 'filter.p.m.custom.experience_level',
      answers: [
        {
          label: 'Someone just starting out',
          note: 'First blaster, easy to use out of the box',
          short: 'Starter',
          values: ['Starter']
        },
        {
          label: "Someone who's played before",
          note: 'Knows the basics and wants a step up',
          short: 'Intermediate',
          values: ['Intermediate']
        },
        {
          label: 'An experienced player',
          note: 'Comfortable with upgrades and maintenance',
          short: 'Advanced',
          values: ['Advanced']
        }
      ]
    },
    {
      key: 'budget',
      rail: 'Budget',
      title: "What's your budget?",
      filter: 'price',
      answers: [
        { label: 'Under $300', note: 'Solid starters and everyday blasters', short: 'Under $300', min: 0, max: 300 },
        { label: '$300 to $700', note: 'Better internals and build', short: '$300 to $700', min: 300, max: 700 },
        { label: '$700 and above', note: 'Premium builds, metal, and gas', short: '$700 and above', min: 700, max: 1e9 }
      ]
    },
    {
      key: 'power',
      rail: 'Power',
      title: 'How should it be powered?',
      filter: 'filter.p.m.custom.power_source',
      answers: [
        { label: 'Electric', note: 'Battery powered - press and go', short: 'Electric', values: ['Electric'] },
        { label: 'Gas', note: 'Realistic blowback and recoil', short: 'Gas', values: ['Gas'] },
        { label: 'Manual', note: 'No battery or gas to worry about', short: 'Manual', values: ['Manual'] }
      ]
    },
    {
      key: 'material',
      rail: 'Build',
      title: 'What should it be built from?',
      filter: 'filter.p.m.custom.build_material',
      answers: [
        { label: 'Full metal', note: 'Heavy, premium, built to last', short: 'Full metal', values: ['Full Metal'] },
        { label: 'Nylon', note: 'Lighter to carry and easier on the wallet', short: 'Nylon', values: ['Nylon'] },
        {
          label: 'A mix of both',
          note: 'Metal where it counts, nylon where it helps',
          short: 'Metal + nylon',
          values: ['Full Metal + Nylon']
        }
      ]
    },
    {
      key: 'fps',
      rail: 'Velocity',
      title: 'How much power do you want?',
      // FPS is free text on the product, so it is banded here. The storefront filter takes the
      // text values themselves - see browseUrl for how the link reproduces the same band.
      filter: 'filter.p.m.custom.key_specs_fps',
      byText: true,
      answers: [
        { label: 'Casual', note: 'Under 250 FPS - backyard and indoor friendly', short: 'Under 250 FPS', min: 0, max: 250 },
        { label: 'Standard', note: '250 to 320 FPS - the usual field range', short: '250 to 320 FPS', min: 250, max: 320 },
        { label: 'High', note: '320 FPS and above - serious outdoor play', short: '320+ FPS', min: 320, max: 1e9 }
      ]
    }
  ];

  // A mark per rank. Three identical grey pills said nothing about why a card was there;
  // a star, a bolt and a tag do, before the words are read.
  var RANK_ICON = {
    best: '<path d="M12 3l2.1 5.9L20 11l-5.9 2.1L12 19l-2.1-5.9L4 11l5.9-2.1z"/>',
    alt: '<path d="M13 2L5 14h6l-1 8 8-12h-6z"/>',
    budget: '<path d="M4 12.5l8-8.5h7v7l-8 8.5z"/><circle cx="15.3" cy="8.7" r="1.2"/>'
  };

  function rankIcon(kind) {
    return '<svg class="fmb-badge-ico" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (RANK_ICON[kind] || '') + '</svg>';
  }

  function fpsNumber(v) {
    var m = String(v || '').match(/(\d{2,3})/);
    return m ? parseInt(m[1], 10) : null;
  }

  function matches(product, question, answer) {
    if (question.key === 'budget') {
      return product.price >= answer.min && product.price < answer.max;
    }
    if (question.key === 'fps') {
      var n = fpsNumber(product.fps);
      return n !== null && n >= answer.min && n < answer.max;
    }
    var field = question.key === 'experience' ? product.experience
      : question.key === 'power' ? product.power
      : product.material;
    return answer.values.indexOf(field) !== -1;
  }

  function money(n) {
    return '$' + Number(n).toFixed(2);
  }

  // Every title in this collection ends with the same three words. Inside a quiz about gel
  // blasters that is a wasted line of wrapping, so it is dropped from the card - the link
  // still goes to the product under its real name.
  function cleanTitle(t) {
    // Not anchored to the end: several titles carry a parenthetical after the suffix, as in
    // "... 1911 Gas Pistol - Gel Blaster (Green Gas)".
    return String(t || '').replace(/\s*[-–]\s*gel\s*blaster\b/i, '').replace(/\s{2,}/g, ' ').trim();
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  var root, dialog, body, kicker, title, rail, bar, tally, tallyLabel;
  var backBtn, nextBtn, browseBtn, restartBtn;
  var products = null;
  var step = 0;
  var picked = [];
  // Which steps were decided for the visitor because only one option was left. They are
  // skipped rather than shown, and they are not jumpable from the rail - landing on one
  // would just skip forward again.
  var auto = [];

  // Usage beacons (no PII). sendBeacon with a JSON string is a CORS-safe POST.
  var BEACON_URL = 'https://sold-out-sorter.vercel.app/api/unsubscribe?fev=1';
  // Per-open dedupe so the funnel counts reached steps, not re-renders.
  var sent = {};
  var pending = [];
  function beacon(p) { pending.push(p); } // queues; flush() sends one batched beacon
  function beaconOnce(key, p) { if (sent[key]) return; sent[key] = true; beacon(p); }
  function flush() {
    if (!pending.length) return;
    var batch = pending; pending = [];
    try { if (navigator.sendBeacon) navigator.sendBeacon(BEACON_URL, JSON.stringify({ batch: batch })); } catch (e) {}
  }
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') flush(); });

  // Cart attributes ride to the ORDER (same path as _origin), so they survive
  // however the shopper adds. This is what makes the quiz answers + the product
  // they picked visible per order in the app — more reliable than the _src line
  // property (which only lands on a native add from the tagged PDP form).
  // keepalive so the write still completes when a "Choose options" link navigates.
  function cartAttrs(attrs) {
    try {
      fetch('/cart/update.js', {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () {});
    } catch (e) {}
  }
  // "Player: Advanced · Budget: $300 to $700 · Power: Gas · …" from the answers so far.
  function answersText() {
    var parts = [];
    for (var i = 0; i < QUESTIONS.length; i++) {
      if (picked[i] == null) continue;
      var a = QUESTIONS[i].answers[picked[i]];
      if (a) parts.push(QUESTIONS[i].rail + ': ' + a.short);
    }
    return parts.join(' · ');
  }

  function el(sel) {
    return root.querySelector(sel);
  }

  /**
   * @param {Element} [host] The element holding the quiz markup. Defaults to the dialog's
   *   own root, so every existing caller keeps working unchanged.
   */
  function bind(host) {
    var target = host || document.querySelector('[data-fmb-root]');
    if (!target) return false;
    if (root === target) return true;
    root = target;
    dialog = el('[data-fmb-dialog]');
    body = el('[data-fmb-body]');
    kicker = el('[data-fmb-kicker]');
    title = el('[data-fmb-title]');
    rail = el('[data-fmb-rail]');
    bar = el('[data-fmb-bar]');
    tally = el('[data-fmb-tally]');
    tallyLabel = el('[data-fmb-tally-label]');
    backBtn = el('[data-fmb-back]');
    nextBtn = el('[data-fmb-next]');
    browseBtn = el('[data-fmb-browse]');
    restartBtn = el('[data-fmb-restart]');

    // Mounted inline there is no dialog and nothing to close, so both are optional.
    var closeBtn = el('[data-fmb-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);
    backBtn.addEventListener('click', back);
    restartBtn.addEventListener('click', restart);
    nextBtn.addEventListener('click', next);
    if (dialog) {
      dialog.addEventListener('close', function () { unlock(); flush(); });
      // Clicking the backdrop lands on the dialog itself; the shell swallows its own clicks.
      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) close();
      });
    }
    body.addEventListener('click', function (e) {
      // Any interaction with a result card (add, view, choose options, title) records
      // the product they pursued from the quiz, as a cart attribute on the order.
      var card = e.target.closest ? e.target.closest('.fmb-pick') : null;
      if (card && card.dataset.pick) cartAttrs({ _fmb_pick: card.dataset.pick });
      var add = e.target.closest ? e.target.closest('[data-fmb-add]') : null;
      if (add) {
        e.preventDefault();
        quickAdd(add);
      }
    });

    if (!dialog) {
      /*
       * Mounted inside the cart drawer. Horizon closes a drawer on any click it cannot
       * find inside itself - isClickedOutside() in assets/utilities.js is
       * `!element.contains(event.target)`. Answering a question re-renders the body, so
       * the button that was clicked is detached before the click reaches the drawer, the
       * drawer reads it as a click on the backdrop, and the whole cart shuts.
       *
       * Every handler the quiz needs is bound inside this root, so the click has nothing
       * left to do by the time it gets here.
       */
      root.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }

    bar.innerHTML = QUESTIONS.map(function () { return '<span></span>'; }).join('');
    return true;
  }

  function lock() {
    document.documentElement.classList.add('fmb-locked');
  }

  function unlock() {
    document.documentElement.classList.remove('fmb-locked');
  }

  /**
   * The collection the quiz browses, declared on the dialog's root like the feed. An inline
   * host (the empty cart) does not repeat it, and without this fallback "See all that fit"
   * there pointed at /collections/undefined.
   */
  function collectionHandle() {
    if (root && root.dataset.collection) return root.dataset.collection;
    var modal = document.querySelector('[data-fmb-root]');
    return (modal && modal.dataset.collection) || 'all';
  }

  /** The feed is declared on the dialog's root; an inline host does not repeat it. */
  function feedUrl() {
    if (root && root.dataset.feed) return root.dataset.feed;
    var modal = document.querySelector('[data-fmb-root]');
    return modal && modal.dataset.feed;
  }

  /** Draw the quiz into whatever is currently bound, loading the catalogue once. */
  function start() {
    sent = {};
    beaconOnce('open', { f: 'fmb', e: 'open' });
    if (products) return render(1);
    // Fetched on first use only, so a visitor who never takes the quiz pays nothing for it.
    // No Accept: application/json here - that makes Shopify answer with its own built-in
    // collection JSON and ignore view=quiz-data, which returns a payload with no products.
    fetch(feedUrl())
      .then(function (r) { return r.json(); })
      .then(function (d) {
        // The collection also holds a fidget toy and a water pistol, and both were winning
        // the "spend less" slot purely by being the cheapest things in it. A real blaster
        // carries an experience level and a readable FPS figure; the toy has neither and the
        // water pistol has FPS "N/A". That pair of checks drops exactly those two and keeps
        // every blaster.
        products = ((d && d.products) || []).filter(function (p) {
          return p.experience && fpsNumber(p.fps) !== null;
        });
        render(1);
      })
      .catch(function () {
        body.innerHTML = '<p class="fmb-empty">We could not load the blasters just now. Please close this and try again.</p>';
      });
  }

  function open() {
    // An inline mount may hold the binding; the dialog is always the modal's own root.
    var modal = document.querySelector('[data-fmb-root]');
    if (!modal || !bind(modal)) return;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    lock();
    start();
  }

  /**
   * Run the quiz inside `host` instead of the dialog - the empty cart drawer asks its
   * questions in place rather than opening a modal over itself. A fresh mount starts at
   * question one: somebody opening an empty cart is at the beginning.
   */
  function mount(host) {
    if (!host || !bind(host)) return;
    step = 0;
    picked = [];
    auto = [];
    start();
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
    unlock();
    flush();
  }

  function restart() {
    sent = {};
    step = 0;
    picked = [];
    auto = [];
    render(1);
  }

  // Products still in play after the answers given so far.
  function remaining(upTo) {
    var list = products || [];
    for (var i = 0; i < upTo; i++) {
      if (picked[i] == null) continue;
      var q = QUESTIONS[i];
      var a = q.answers[picked[i]];
      list = list.filter(function (p) { return matches(p, q, a); });
    }
    return list;
  }

  // The answers that still lead somewhere at this step, with the count each one would leave.
  function offeredAt(index) {
    var q = QUESTIONS[index];
    var pool = remaining(index);
    var out = [];
    q.answers.forEach(function (a, i) {
      var n = pool.filter(function (p) { return matches(p, q, a); }).length;
      if (n > 0) out.push({ answer: a, index: i, count: n });
    });
    return out;
  }

  function paintRail() {
    rail.innerHTML = QUESTIONS.map(function (q, i) {
      var state = i < step ? 'done' : i === step ? 'now' : 'todo';
      var chosen = picked[i] != null ? QUESTIONS[i].answers[picked[i]].short : '';
      var jump = state === 'done' && !auto[i];
      return '<li>' +
        '<button type="button" class="fmb-rail-step" data-state="' + state + '"' +
        ' data-can-jump="' + jump + '" data-step="' + i + '"' + (jump ? '' : ' disabled') + '>' +
        '<span class="fmb-step-dot">' + (state === 'done' ? '&#10003;' : i + 1) + '</span>' +
        '<span class="fmb-step-text">' +
        '<span class="fmb-step-name">' + q.rail + '</span>' +
        (chosen
          ? '<span class="fmb-step-val">' + esc(chosen) +
            (auto[i] ? ' <span class="fmb-step-auto">(only option)</span>' : '') + '</span>'
          : '') +
        '</span></button></li>';
    }).join('');

    Array.prototype.forEach.call(rail.querySelectorAll('[data-can-jump="true"]'), function (b) {
      b.addEventListener('click', function () {
        step = Number(b.dataset.step);
        render(-1);
      });
    });

    Array.prototype.forEach.call(bar.children, function (node, i) {
      node.dataset.state = i < step ? 'done' : i === step ? 'now' : '';
    });
  }

  // The number counts to its new value. Watching 151 fall to 23 is the clearest signal the
  // quiz is doing something, and it costs one rAF loop.
  function setTally(n, label) {
    tallyLabel.textContent = label;
    var from = parseInt(tally.textContent, 10);
    if (calmly || isNaN(from) || from === n) {
      tally.textContent = n;
      return;
    }
    var t0 = performance.now();
    (function frame(t) {
      var k = Math.min(1, (t - t0) / 420);
      var eased = 1 - Math.pow(1 - k, 3);
      tally.textContent = Math.round(from + (n - from) * eased);
      if (k < 1) requestAnimationFrame(frame);
    })(t0);
  }


  /**
   * @param {number} dir 1 when moving forward, -1 when moving back. It decides which way a
   *   question with a single surviving option is skipped past, so Back never lands on one
   *   and bounces straight forward again.
   */
  function render(dir) {
    dir = dir || 1;
    if (step < 0) { step = 0; dir = 1; }
    if (step >= QUESTIONS.length) return renderResults();

    var q = QUESTIONS[step];
    var offered = offeredAt(step);

    if (!offered.length) {
      step = QUESTIONS.length;
      return renderResults();
    }

    // One option is not a question. It is answered for the visitor and the rail records it,
    // rather than making them press Next on a choice they do not have.
    if (offered.length === 1) {
      picked[step] = offered[0].index;
      picked.length = Math.max(picked.length, step + 1);
      auto[step] = true;
      step += dir;
      if (step < 0) { step = 0; dir = 1; }
      return render(dir);
    }
    auto[step] = false;
    beaconOnce('step' + (step + 1), { f: 'fmb', e: 'step', n: step + 1 });
    if (picked[step] == null) picked[step] = offered[0].index;

    var pool = remaining(step);
    // Choosing an answer narrows the tally and the thumbnails straight away, rather than
    // waiting for Next. The number is the reason to keep going, so it should answer the tap.
    var chosen = picked[step] == null ? null : offered.filter(function (o) {
      return o.index === picked[step];
    })[0];
    var showing = chosen
      ? pool.filter(function (p) { return matches(p, q, chosen.answer); })
      : pool;

    kicker.textContent = 'Question ' + (step + 1) + ' of ' + QUESTIONS.length;
    title.textContent = q.title;
    setTally(showing.length, showing.length === 1 ? 'Blaster left' : 'Blasters left');
    paintRail();
    backBtn.hidden = step === 0;
    // Start over is an answer to "these are not the blasters I wanted", so it waits for
    // the results. Offering it mid-quiz only invites losing two answers by accident.
    restartBtn.hidden = true;
    nextBtn.hidden = false;
    browseBtn.hidden = true;

    body.innerHTML = '';
    offered.forEach(function (o, position) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fmb-opt';
      btn.setAttribute('aria-pressed', picked[step] === o.index ? 'true' : 'false');
      btn.innerHTML =
        '<span class="fmb-mark">' + icon(q.key, o.index) +
        '<span class="fmb-key"><span class="fmb-key-letter">' + KEYS[position] + '</span>' +
        '<svg class="fmb-key-tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
        'stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M5 13l4.5 4.5L19 8"/></svg></span></span>' +
        '<span class="fmb-opt-text"><span class="fmb-opt-label">' + esc(o.answer.label) + '</span>' +
        (o.answer.note ? '<span class="fmb-opt-note">' + esc(o.answer.note) + '</span>' : '') +
        '</span>' +
        '<span class="fmb-n"><b>' + o.count + '</b>' + (o.count === 1 ? 'match' : 'matches') + '</span>' +
        '<span class="fmb-go" aria-hidden="true">&rarr;</span>';
      btn.addEventListener('click', function () {
        picked[step] = o.index;
        beacon({ f: 'fmb', e: 'pick', q: q.key, a: o.answer.short });
        // Later answers were chosen against a different pool, so they are dropped.
        picked.length = step + 1;
        auto.length = step + 1;
        render(1);
      });
      body.appendChild(btn);
    });

    if (offered.length < q.answers.length) {
      var note = document.createElement('p');
      note.className = 'fmb-note';
      note.textContent = 'Options with nothing in stock right now are not shown.';
      body.appendChild(note);
    }

    nextBtn.disabled = picked[step] == null;
    nextBtn.textContent = step === QUESTIONS.length - 1 ? 'Show my matches' : 'Next';
  }

  function next() {
    if (picked[step] == null) return;
    step++;
    render(1);
    body.scrollTop = 0;
  }

  function back() {
    if (step === 0) return;
    step--;
    render(-1);
    body.scrollTop = 0;
  }

  // The collection link behind "See all that fit". It has to land on exactly the blasters
  // the results screen counted as full matches, so the number on the page agrees with the
  // number in the quiz.
  //
  // FPS is free text ("250+ FPS", "230-240 FPS"), so no range can be put in the URL. The
  // storefront filter takes the text values themselves, though, and several values of one
  // filter are OR'd - so the link lists the FPS text of every full match. Any other product
  // with the same text parses to the same number and is in the same band, so nothing extra
  // comes through, and anything with no readable FPS (the water pistol) is left out the same
  // way the quiz leaves it out.
  //
  // Price bands are half-open in the quiz (under $300 means < 300), so the upper bound stops
  // a cent short rather than letting a $300.00 blaster into two bands.
  function browseUrl() {
    var params = ['filter.v.availability=1'];
    var full = remaining(QUESTIONS.length);
    QUESTIONS.forEach(function (q, i) {
      if (picked[i] == null || !q.filter) return;
      var a = q.answers[picked[i]];
      if (q.filter === 'price') {
        params.push('filter.v.price.gte=' + a.min);
        if (a.max < 1e9) params.push('filter.v.price.lte=' + (a.max - 0.01).toFixed(2));
        return;
      }
      var values = q.byText
        ? full.map(function (p) { return p.fps; }).filter(function (v, k, all) { return all.indexOf(v) === k; })
        : a.values;
      values.forEach(function (v) {
        params.push(q.filter + '=' + encodeURIComponent(v));
      });
    });
    return '/collections/' + collectionHandle() + '?' + params.join('&');
  }

  function renderResults() {
    step = QUESTIONS.length;
    kicker.textContent = 'Your matches';
    title.textContent = 'Here is what fits';
    paintRail();
    backBtn.hidden = false;
    restartBtn.hidden = false;
    nextBtn.hidden = true;

    // Scored against every answer, so a near miss can still surface when nothing matches
    // all five.
    var scored = (products || []).map(function (p) {
      var score = 0;
      var chips = [];
      QUESTIONS.forEach(function (q, i) {
        if (picked[i] == null) return;
        var a = q.answers[picked[i]];
        var hit = matches(p, q, a);
        if (hit) score++;
        chips.push({ text: a.short, hit: hit });
      });
      return { p: p, score: score, chips: chips };
    }).sort(function (a, b) {
      return b.score - a.score || a.p.price - b.p.price;
    });

    var fullMatches = remaining(QUESTIONS.length).length;
    setTally(fullMatches, 'Full matches');
    beaconOnce('done', { f: 'fmb', e: 'done', m: fullMatches });
    flush();

    if (!scored.length) {
      body.innerHTML = '<p class="fmb-empty">Nothing matched. Step back and widen an answer.</p>';
      browseBtn.hidden = true;
      return;
    }

    var best = scored[0];
    // Record what they answered + the top recommendation, onto the cart (→ order).
    // A later click on a specific card overwrites _fmb_pick with the one they chose.
    cartAttrs({ _fmb_answers: answersText(), _fmb_pick: cleanTitle(best.p.title) });
    // A second and third pick have to be near the first to be worth showing. Without this
    // the cheapest thing in the collection won the "spend less" slot on price alone - a
    // revolver that matched none of the five answers, every tag struck through, presented
    // as a recommendation. Two honest cards beat three with a filler.
    var floor = Math.max(1, best.score - 1);
    var rest = scored.slice(1).filter(function (s) { return s.score >= floor; });

    // Only call it more powerful when it genuinely is, otherwise the badge is a claim the
    // product does not back up.
    var stronger = rest.filter(function (s) {
      return fpsNumber(s.p.fps) > fpsNumber(best.p.fps);
    })[0];
    var alt = stronger || rest[0];
    var budget = rest.filter(function (s) {
      return s.p.price < best.p.price && s !== alt;
    }).sort(function (a, b) { return a.p.price - b.p.price; })[0];

    var cards = [
      { kind: 'best', badge: 'Best match', s: best },
      { kind: 'alt', badge: stronger ? 'More power' : 'Also consider', s: alt },
      { kind: 'budget', badge: 'Spend less', s: budget }
    ].filter(function (c) { return c.s; });

    body.innerHTML = '<div class="fmb-res">' + cards.map(function (c) {
      var p = c.s.p;
      var was = p.compareAt > p.price ? '<span class="fmb-was">' + money(p.compareAt) + '</span>' : '';
      // Four figures, not seven. FPS is already stamped on the shot, and weight, battery
      // and the box contents are a product-page job - a card choosing between three
      // blasters only has to separate them.
      var rows = [
        ['Range', p.range],
        ['Fire modes', p.modes],
        ['Build', p.material],
        ['Power', p.power]
      ].filter(function (r) {
        return r[1] && String(r[1]).trim() && !/^n\/?a$/i.test(String(r[1]).trim());
      });
      var table = rows.length
        ? '<dl class="fmb-spec">' + rows.map(function (r) {
            return '<div class="fmb-spec-row"><dt>' + esc(r[0]) + '</dt><dd>' + esc(r[1]) + '</dd></div>';
          }).join('') + '</dl>'
        : '';

      var missed = c.s.chips.filter(function (ch) { return !ch.hit; });
      var note = missed.length
        ? '<p class="fmb-miss">Differs from your pick on ' +
          missed.map(function (ch) { return esc(ch.text.toLowerCase()); }).join(', ') + '</p>'
        : '';

      var fpsRaw = String(p.fps || '').trim();
      var fpsParts = fpsRaw.match(/^(.*?)\s*(fps)\s*$/i);
      var chrono = fpsRaw && !/^n\/?a$/i.test(fpsRaw)
        ? '<span class="fmb-chrono">' +
          '<svg class="fmb-chrono-ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
          'stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
          '<path d="M3.6 18a8.8 8.8 0 1116.8 0"/><path d="M12 18l4.6-4.9"/></svg>' +
          '<b>' + esc(fpsParts ? fpsParts[1] : fpsRaw) +
          '<i>' + esc(fpsParts ? fpsParts[2] : 'FPS') + '</i></b></span>'
        : '';

      var action = p.oneVariant && p.variantId
        ? '<button class="fmb-btn fmb-btn--primary fmb-add" type="button" data-fmb-add data-variant="' +
          p.variantId + '">Add to cart <span aria-hidden="true">&rarr;</span></button>'
        : '<a class="fmb-btn fmb-btn--primary fmb-add" href="' + esc(p.url) + '?_src=find-my-blaster' +
          '">Choose options <span aria-hidden="true">&rarr;</span></a>';

      return '<article class="fmb-pick' + (c.kind === 'best' ? ' fmb-pick--best' : '') + '" data-pick="' + esc(cleanTitle(p.title)) + '">' +
        '<a class="fmb-pick-media" href="' + esc(p.url) + '?_src=find-my-blaster" tabindex="-1" aria-hidden="true">' +
        (p.image
          ? '<img class="fmb-pick-img" src="' + esc(p.image) + '" alt="" loading="lazy" width="150" height="150">'
          : '<span class="fmb-pick-img"></span>') +
        '<span class="fmb-badge" data-kind="' + c.kind + '">' + rankIcon(c.kind) + c.badge + '</span>' + chrono +
        '</a>' +
        '<div class="fmb-pick-body">' +
        '<h3><a href="' + esc(p.url) + '?_src=find-my-blaster">' + esc(cleanTitle(p.title)) + '</a></h3>' +
        '<p class="fmb-price">' + money(p.price) + was + '</p>' +
        table + note +
        '<div class="fmb-actions">' + action +
        '<a class="fmb-btn fmb-btn--secondary" href="' + esc(p.url) + '?_src=find-my-blaster">View</a></div>' +
        '</div></article>';
    }).join('') + '</div>';

    browseBtn.href = browseUrl();
    browseBtn.hidden = false;
    body.scrollTop = 0;
  }

  // Quick add goes through the same bridge the product-page blocks use, so the item lands in
  // Horizon's cart and its drawer updates. The quiz closes on success: showModal() makes the
  // rest of the page inert, so a drawer opened underneath could not be used until it does.
  function quickAdd(btn) {
    var id = Number(btn.dataset.variant);
    if (!id) return;
    if (!window.GBUCartSync) {
      window.location.href = '/cart/' + id + ':1';
      return;
    }
    btn.dataset.state = 'busy';
    btn.disabled = true;
    btn.textContent = 'Adding…';
    window.GBUCartSync.add([{ id: id, quantity: 1, properties: { _src: 'find-my-blaster' } }])
      .then(function () {
        btn.dataset.state = 'done';
        btn.textContent = 'Added';
        beaconOnce('add', { f: 'fmb', e: 'add' });
        setTimeout(close, 420);
      })
      .catch(function () {
        btn.dataset.state = 'error';
        btn.disabled = false;
        btn.textContent = 'Could not add - try the page';
      });
  }

  // Delegated, so a launcher added anywhere later - header, a tile, the cart drawer - works
  // without being wired up.
  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-fmb-open]') : null;
    if (!t) return;
    e.preventDefault();
    open();
  });

  document.addEventListener('open-quiz', open);

  document.addEventListener('keydown', function (e) {
    if (!dialog || !dialog.open || step >= QUESTIONS.length) return;
    var i = KEYS.indexOf(String(e.key || '').toUpperCase());
    if (i === -1) return;
    var opts = body.querySelectorAll('.fmb-opt');
    if (!opts[i]) return;
    e.preventDefault();
    opts[i].click();
  });

  document.addEventListener('DOMContentLoaded', function () {
    if (!root) bind();
  });
  if (document.readyState !== 'loading') bind();

  // Exposed so a future launcher can reset the quiz instead of resuming it mid-answer.
  window.gbuFindMyBlaster = { open: open, close: close, restart: restart, mount: mount };

  /**
   * The cart drawer renders an inline host when the cart is empty, and renders it again
   * whenever the cart changes - so the host can arrive long after this script ran.
   *
   * Mounting waits until the host is actually on screen. The drawer's markup sits in every
   * page whether or not it has been opened, and mounting on sight alone would fetch the
   * catalogue for a drawer nobody touched.
   */
  var seen = 'data-fmb-watched';

  function watchPanels() {
    var hosts = document.querySelectorAll('[data-fmb-panel]:not([' + seen + '])');
    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      host.setAttribute(seen, '1');
      if (!window.IntersectionObserver) {
        mount(host);
        continue;
      }
      new IntersectionObserver(function (entries, obs) {
        for (var k = 0; k < entries.length; k++) {
          if (!entries[k].isIntersecting) continue;
          obs.disconnect();
          mount(entries[k].target);
        }
      }).observe(host);
    }
  }

  new MutationObserver(watchPanels).observe(document.documentElement, { childList: true, subtree: true });
  watchPanels();
})();
